import {Injectable} from '@angular/core';
import {FuseNavigationItem} from '@fuse/components/navigation';
import {FuseMockApiService} from '@fuse/lib/mock-api';
import {hasServiceContractsV2Access} from '@shared/helpers/service-contracts';
import {AuthService} from '@shared/services/auth.service';
import {compactNavigation, futuristicNavigation, horizontalNavigation} from 'app/mock-api/common/navigation/data';
import {cloneDeep} from 'lodash-es';
import {
    accountingNavItems,
    aiNavItems,
    canAccessTeamChat,
    canShowCallAgentNavigation,
    canShowMailboxNavigation,
    canShowRootPeriodsNavigation,
    canShowVatReportNavigation,
    communicationNavItems,
    navigationPermissions,
    productivityNavItems,
    settingsNavItems,
    subsNavItems,
    supportNavItems,
    technikNavItems,
    vermietungNavItems,
    verwaltungNavItems
} from '../../../navigation/nahaus.navigation';
import {filterNavigationByPermissions} from '../../../navigation/navigation-permissions.util';
import {UserRoleOptions} from '@users/models/user.interface';

@Injectable({
    providedIn: 'root'
})
export class NavigationMockApi {

    nahausNavigation: FuseNavigationItem[] = [
        ...verwaltungNavItems,
        ...aiNavItems,
        ...vermietungNavItems,
        ...accountingNavItems,
        ...communicationNavItems,
        ...technikNavItems,
        ...productivityNavItems,
        ...subsNavItems,
        ...supportNavItems,
        // ...assetsNavItems,
        ...settingsNavItems
        // {
        //   id: 'example',
        //   title: 'Example',
        //   type: 'basic',
        //   icon: 'heroicons_outline:chart-pie',
        //   link: '/example'
        // },
    ];

    private readonly _compactNavigation: FuseNavigationItem[] = compactNavigation;
    private readonly _defaultNavigation: FuseNavigationItem[] = this.nahausNavigation;
    private readonly _futuristicNavigation: FuseNavigationItem[] = futuristicNavigation;
    private readonly _horizontalNavigation: FuseNavigationItem[] = horizontalNavigation;

    /**
     * Constructor
     */
    constructor(private _fuseMockApiService: FuseMockApiService,
                private authService: AuthService) {
        // Register Mock API handlers
        this.registerHandlers();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Register Mock API handlers
     */
    registerHandlers(): void {
        // -----------------------------------------------------------------------------------------------------
        // @ Navigation - GET
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onGet('api/common/navigation')
            .reply(() => {

                // Fill compact navigation children using the default navigation
                this._compactNavigation.forEach((compactNavItem) => {
                    this._defaultNavigation.forEach((defaultNavItem) => {
                        if (defaultNavItem.id === compactNavItem.id) {
                            compactNavItem.children = cloneDeep(defaultNavItem.children);
                        }
                    });
                });

                // Fill futuristic navigation children using the default navigation
                this._futuristicNavigation.forEach((futuristicNavItem) => {
                    this._defaultNavigation.forEach((defaultNavItem) => {
                        if (defaultNavItem.id === futuristicNavItem.id) {
                            futuristicNavItem.children = cloneDeep(defaultNavItem.children);
                        }
                    });
                });

                // Fill horizontal navigation children using the default navigation
                this._horizontalNavigation.forEach((horizontalNavItem) => {
                    this._defaultNavigation.forEach((defaultNavItem) => {
                        if (defaultNavItem.id === horizontalNavItem.id) {
                            horizontalNavItem.children = cloneDeep(defaultNavItem.children);
                        }
                    });
                });

                // Return the response
                const customerID = this.authService.customerID;

                return [
                    200,
                    {
                        compact: this.filterNavigation(cloneDeep(this._compactNavigation), customerID),
                        default: this.filterNavigation(cloneDeep(this._defaultNavigation), customerID),
                        futuristic: this.filterNavigation(cloneDeep(this._futuristicNavigation), customerID),
                        horizontal: this.filterNavigation(cloneDeep(this._horizontalNavigation), customerID)
                    }
                ];
            });
    }

    /**
     * Applies every navigation filter (customer availability + user permissions) in order.
     */
    private filterNavigation(items: FuseNavigationItem[], customerID?: string | null): FuseNavigationItem[] {
        const forCustomer = this.filterNavigationForCustomer(items, customerID);

        // Owners/Admins/Master keep the full navigation (same "green pass" the route guards honor).
        if (this.authService.hasGreenPass()) {
            return forCustomer;
        }

        return filterNavigationByPermissions(
            forCustomer,
            this.authService.firestoreUser?.permissions ?? [],
            navigationPermissions
        ) as FuseNavigationItem[];
    }

    private filterNavigationForCustomer(items: FuseNavigationItem[], customerID?: string | null): FuseNavigationItem[] {
        return (items || [])
            .map((item) => ({
                ...item,
                children: item.children ? this.filterNavigationForCustomer(item.children, customerID) : item.children
            }))
            .filter((item) => item.id !== 'apps.callAgent' || canShowCallAgentNavigation(customerID))
            .filter((item) => item.id !== 'feature.serviceContracts' || hasServiceContractsV2Access(customerID))
            .filter((item) => item.id !== 'apps.mailbox' || canShowMailboxNavigation(customerID))
            .filter((item) => item.id !== 'feature.periods' || canShowRootPeriodsNavigation(customerID))
            .filter((item) => item.id !== 'apps.chat' || canAccessTeamChat())
            .filter((item) => item.id !== 'accounting-vat' || canShowVatReportNavigation(customerID))
            // API-Schnittstelle: mirror canAccessApiGuard — visible for master/admin (green pass) or API_USER
            .filter((item) => item.id !== 'settings.api'
                || this.authService.hasGreenPass()
                || this.authService.firestoreUser?.role === UserRoleOptions.API_USER);
    }
}
